import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { navigationRoutes } from '@app/utils/navigation-routes';
import TimeDuration from '@app/pages/analytics/project-records/blocks/time-duration';
import { Paginator, Skeleton, TooltipWrapper } from '@ui';
import { FilterTableData, TableItem, TableTitle } from '@app/components/table/table.type';
import useTranslation from '@app/hooks/use-translation';
import { PaginationResponse } from '@app/components/ui/paginator/paginator';
import { DownloadCloudIcon, Oscilogram } from '@app/components/ui/icons/icons-list';
import { Filter, Range } from '@app/components/ui/easy-filter/types';
import { useGetLiveReportRecordsQuery } from '@app/store/api/live-reports.api';
import { useLazyDownloadRecordQuery } from '@app/store/api/records.api';
import { useAppSelector } from '@app/store/store';
import { useUpdateUserSettingsMutation } from '@app/store/api/user-settings.api';
import { ChartsLayoutType } from '@app/interfaces/dashboards.type';
import Table from '@app/components/table';
import { MetricHeaderItem, MetricMetricItemData } from '@app/interfaces/metric.type';
import { useLazyGetProjectMetricQuery } from '@app/store/api/metrics.api';
import MetricTableValueBuild from '@app/components/metric-table-value-build';
import { useOutletContext } from 'react-router';

import { useGetChatLiveReportRecordsQuery } from '@app/store/api/chat-api/live-reports.api';

import { useLazyGetChatProjectMetricQuery } from '@app/store/api/chat-api/chat-metric.api';

import { useLazyDownloadChatQuery } from '@app/store/api/chat-api/all-chats.api';

import { ReportOutletContext } from '../report.outlet';

const DEFAULT_REPORT_RECORDS_LIMIT = 10;
const DEFAULT_REPORT_RECORDS_OFFSET = 0;

type ReportRecordsTableBlockProps = {
  filter: Filter & Range;
  hide: boolean;
};

const ReportRecordsTableBlock: FC<ReportRecordsTableBlockProps> = (props) => {
  const { filter, hide } = props;
  const { t } = useTranslation('pages.reports');
  const { liveReport, currentTemplate } = useOutletContext<ReportOutletContext>();
  const reportId = liveReport?.live_report_id || '';
  const { userSettings } = useAppSelector((state) => state.userSettings);

  const [reportRecordsLimit, changeReportRecordsLimit] = useState<number>(
    userSettings?.tablesLimit?.reports?.reportRecords || DEFAULT_REPORT_RECORDS_LIMIT,
  );
  const [metricSettings, updateMetricSettings] = useState<{
    [metric_id: string]: MetricHeaderItem;
  }>({});
  const [reportRecordsOffset, changeReportRecordsOffset] = useState<number>(
    DEFAULT_REPORT_RECORDS_OFFSET,
  );
  const [reportRecordsOrderBy, changeRecordsReportOrderBy] = useState<FilterTableData>();

  // api
  const [updateUserSettings, { isLoading: userLoading }] = useUpdateUserSettingsMutation();
  const { data: voiceLiveReportRecords, isLoading: voiceRecordsLoading } =
    useGetLiveReportRecordsQuery(
      {
        params: { id: reportId },
        body: {
          offset: reportRecordsOffset,
          limit: reportRecordsLimit,
          filter,
          ...reportRecordsOrderBy,
        },
      },
      { skip: currentTemplate === 'chat' || !reportId },
    );
  const { data: chatLiveReportRecords, isLoading: chatRecordsLoading } =
    useGetChatLiveReportRecordsQuery(
      {
        params: { id: reportId },
        body: {
          offset: reportRecordsOffset,
          limit: reportRecordsLimit,
          filter,
          ...reportRecordsOrderBy,
        },
      },
      { skip: currentTemplate === 'voice' || !reportId },
    );
  const recordsLoading = voiceRecordsLoading || chatRecordsLoading;
  const liveReportRecords = useMemo(
    () => ({
      chat: chatLiveReportRecords,
      voice: voiceLiveReportRecords,
    }),
    [chatLiveReportRecords, voiceLiveReportRecords],
  );
  const liveReportRecordsArrayForMap = useMemo(
    () => ({
      chat: chatLiveReportRecords?.chats,
      voice: voiceLiveReportRecords?.records,
    }),
    [chatLiveReportRecords, voiceLiveReportRecords],
  );

  const [getProjectMetricVoice, { isLoading: metricLoadingVoice }] = useLazyGetProjectMetricQuery();
  const [getProjectMetricChat, { isLoading: metricLoadingChat }] =
    useLazyGetChatProjectMetricQuery();
  const metricLoading = metricLoadingVoice || metricLoadingChat;
  const getProjectMetric = useMemo(
    () => ({ chat: getProjectMetricChat, voice: getProjectMetricVoice }),
    [getProjectMetricChat, getProjectMetricVoice],
  );
  const [downloadRecord] = useLazyDownloadRecordQuery();
  const [downloadChat] = useLazyDownloadChatQuery();
  const downloadHandler = useCallback(
    (id: string) => {
      if (currentTemplate === 'chat') {
        downloadChat({ chat_id: id });
      } else {
        downloadRecord({ record_id: id });
      }
    },
    [currentTemplate, downloadChat, downloadRecord],
  );

  // api

  const [userSettingsLoading, setUserSettingsLoading] = useState(false);
  useEffect(() => {
    if (userLoading) {
      setUserSettingsLoading(true);
    }
    if (!userLoading) {
      setTimeout(() => setUserSettingsLoading(false), 300);
    }
  }, [userLoading]);

  useEffect(() => {
    changeReportRecordsLimit(userSettings?.tablesLimit?.reports?.reportRecords || 10);
  }, [userSettings?.tablesLimit?.reports?.reportRecords]);

  useEffect(() => {
    if (!liveReportRecords) return;
    if ((liveReportRecords[currentTemplate || 'voice']?.total || 0) <= reportRecordsLimit) {
      changeReportRecordsOffset(0);
    }
  }, [currentTemplate, liveReportRecords, reportRecordsLimit]);

  useEffect(() => {
    if (!liveReportRecords[currentTemplate || 'voice'] || !liveReport) return;

    updateMetricSettings(
      liveReportRecords[currentTemplate || 'voice']?.headers.reduce(
        (reportHeaderResult, currentMetric) => {
          reportHeaderResult[currentMetric.metric_id] = currentMetric;
          if (currentMetric.type === 'tags') {
            getProjectMetric[currentTemplate || 'voice']({
              project_id: liveReport?.project.project_id,
              metric_id: currentMetric.metric_id,
            })
              .unwrap()
              .then((metricInfoResult) =>
                updateMetricSettings((prev) => ({
                  ...prev,
                  [currentMetric.metric_id]: {
                    ...prev[currentMetric.metric_id],
                    settings: metricInfoResult.settings,
                  },
                })),
              );
          }
          return reportHeaderResult;
        },
        {},
      ) || {},
    );
  }, [currentTemplate, getProjectMetric, liveReport, liveReportRecords]);

  const [tableLayouts, changeTableLayouts] = useState(
    userSettings?.tableColsSize?.reports?.reportRecords,
  );
  const layout = useMemo(
    () => tableLayouts?.find((layoutItem) => layoutItem.id === reportId)?.layout,
    [reportId, tableLayouts],
  );
  useEffect(() => {
    const condition = userSettings?.tableColsSize?.reports?.reportRecords?.find(
      (layoutItem) => layoutItem.id === reportId,
    );
    if (!condition) {
      const systemArr = [
        ...(userSettings?.tableColsSize?.reports?.reportRecords || []),
        { id: reportId, layout: [] },
      ];
      updateUserSettings({
        ...userSettings,
        tableColsSize: {
          ...userSettings?.tableColsSize,
          reports: { ...userSettings?.tableColsSize?.reports, reportRecords: systemArr },
        },
      });
    } else {
      changeTableLayouts(userSettings?.tableColsSize?.reports?.reportRecords);
    }
  }, [reportId, updateUserSettings, userSettings]);

  const checkingForSorting = (index) => {
    if (!liveReportRecords) return;
    return index !== (liveReportRecords[currentTemplate || 'voice']?.headers.length || 0) + 1;
  };

  const recordsLiveReportDataColumns: TableTitle[] = [
    {
      index: 'duration',
      title: t('records_table.duration'),
      size: 160,
    },
    ...(liveReportRecords[currentTemplate || 'voice']?.headers.map((titleData, index) => ({
      index: titleData.metric_id,
      title: titleData.name,
      size: 190,
      filter: checkingForSorting(index),
      disableDefaultHeight: titleData.type === 'tags',
    })) || []),
    { index: 'utils', title: t('records_table.actions'), size: 110 },
  ];

  const idKeyByTemplate = useMemo(() => ({ chat: 'chat_id', voice: 'record_id' }), []);
  const recordPathByTemplate = useMemo(
    () => ({ chat: navigationRoutes.chatProjectRecords, voice: navigationRoutes.projectRecords }),
    [],
  );

  const formattedLiveReportRecordsTableData = liveReportRecordsArrayForMap[
    currentTemplate || 'voice'
  ]?.map((dataItem, index) => ({
    duration: (
      <div className="truncate w-full flex items-center justify-center">
        <div className="!mr-[10px]">
          {/* <Checkbox
            checked={selectedRecords?.includes(record.record_id)}
            onChange={() => onSelectRecord(record.record_id)}
          /> */}
        </div>
        <div className="flex cursor-pointer">
          <TooltipWrapper
            content={t('popup_hints.to_record_watch')}
            id="record_report"
            className={'flex h-full items-center'}
          >
            <TimeDuration
              link={`/${recordPathByTemplate[currentTemplate]}/${liveReport?.project.project_id}/${
                dataItem[idKeyByTemplate[currentTemplate]]
              }`}
              duration={dataItem.duration}
            />
          </TooltipWrapper>
        </div>
      </div>
    ),
    ...Object.fromEntries(
      dataItem.data.map((metric) => [
        [metric.metric_id],
        metricSettings && liveReport ? (
          <MetricTableValueBuild
            index={index}
            page={Math.ceil(reportRecordsOffset / reportRecordsLimit) + 1}
            limit={reportRecordsLimit}
            currentTemplate={currentTemplate}
            metric={metric as MetricMetricItemData}
            recordID={dataItem[idKeyByTemplate[currentTemplate]]}
            projectID={liveReport.project.project_id}
            metricHeader={metricSettings[metric.metric_id]}
            columnWidth={layout?.find((layoutItem) => layoutItem.i === metric.metric_id)?.w || 6}
          />
        ) : (
          <div></div>
        ),
      ]),
    ),
    utils: (
      <div className="flex w-full items-start pl-[19px] pr-[10px]">
        <DownloadCloudIcon
          onClick={() => {
            downloadHandler(dataItem[idKeyByTemplate[currentTemplate]]);
          }}
          hintTitle={t('popup_hints.download_record')}
          size={18}
          className={`mr-[18px] text-3color hover:text-action transition cursor-pointer`}
        />

        <Oscilogram
          onClick={() =>
            window.open(
              `/${recordPathByTemplate[currentTemplate]}/${liveReport?.project.project_id}/${
                dataItem[idKeyByTemplate[currentTemplate]]
              }`,
              '_blank',
            )
          }
          hintTitle={t('popup_hints.to_record_watch')}
          size={22}
          className={'text-3color hover:text-action cursor-pointer transition'}
        />
      </div>
    ),
  }));

  function onChangePagination({ offset, limit }: PaginationResponse) {
    changeReportRecordsOffset(offset);
    updateUserSettings({
      ...userSettings,
      tablesLimit: {
        ...userSettings?.tablesLimit,
        reports: { ...userSettings?.tablesLimit?.reports, reportRecords: limit },
      },
    });
    changeReportRecordsLimit(limit);
  }

  function onChangeLayoutHandler(layout: ChartsLayoutType[]) {
    const currentChangedLayout = tableLayouts?.map((tablesLayout) => {
      if (tablesLayout.id === reportId) {
        return { id: tablesLayout.id, layout: layout.map((item) => ({ ...item, maxW: 24 })) };
      }
      return tablesLayout;
    });

    updateUserSettings({
      ...userSettings,
      tableColsSize: {
        ...userSettings?.tableColsSize,
        reports: { ...userSettings?.tableColsSize?.reports, reportRecords: currentChangedLayout },
      },
    });
  }

  function handleResetTableGrid() {
    updateUserSettings({
      ...userSettings,
      tableColsSize: {
        ...userSettings?.tableColsSize,
        reports: { ...userSettings?.tableColsSize?.reports, reportRecords: [] },
      },
    });
  }

  if (recordsLoading || metricLoading) return <Skeleton height={360} />;
  return (
    <div className={`relative ${hide ? 'hidden' : ''}`}>
      {userSettingsLoading && <Skeleton height={800} className={'absolute z-[999] mt-6 top-0'} />}
      <div className="flex flex-col gap-[10px] pb-2">
        <Table
          columns={recordsLiveReportDataColumns}
          dataSource={formattedLiveReportRecordsTableData as unknown as Array<TableItem>}
          onFilter={changeRecordsReportOrderBy}
          isEmpty={t('system.empty_records')}
          handleResetTableGrid={handleResetTableGrid}
          layout={tableLayouts
            ?.find((layoutItem) => layoutItem.id === reportId)
            ?.layout.map((item) => ({ ...item, maxW: 16 }))}
          onLayoutChange={onChangeLayoutHandler}
        />
        <Paginator
          page={Math.ceil(reportRecordsOffset / reportRecordsLimit) + 1}
          count={liveReportRecords[currentTemplate || 'voice']?.total || 0}
          limit={reportRecordsLimit}
          onChange={onChangePagination}
        />
      </div>
    </div>
  );
};

export default ReportRecordsTableBlock;
